import React, { JSX } from "react";
import * as HeroIcons from "@heroicons/react/20/solid";

import Tooltip from "../tooltip";

import GetHeroIcon from "../../../utils/getHeroIcons";

import "./input.css";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  value?: string;
  error?: boolean;
  inputdisable?: boolean;
  errorMessage?: string;
  register?: (...args: any[]) => void;
  iconName?: keyof typeof HeroIcons;
  iconPosition?: "leading" | "trailing" | undefined;
  iconVariant?: "outline" | "solid";
  optional?: boolean;
  infoicon?: boolean;
  setterFunction?: (value: any) => void;
  infoMessage?: string;
  maxLength?: number;
}

const Input: React.FC<InputProps> = React.forwardRef(
  (
    {
      name,
      label,
      value,
      register,
      error,
      errorMessage,
      iconName,
      iconPosition,
      iconVariant,
      optional,
      infoicon,
      infoMessage,
      inputdisable,
      setterFunction,
      maxLength,
      ...rest
    },
    forwardedRef
  ): JSX.Element => {
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    React.useImperativeHandle(forwardedRef, () => inputRef.current);
    const handleKeyDown = (e: any) => {
      if (e.key == "Enter") {
        e.preventDefault();
      }
    };

    return (
      <div
        className="flex flex-col items-start space-y-1 w-full"
        role="presentation"
      >
        {label && (
          <label className="text-secondary mb-1 font-medium flex ">
            {label}
            {optional && (
              <span className="text-sm ps-2 font-thin">(Optional)</span>
            )}
            {infoicon && (
              <Tooltip
                message={` ${infoMessage}`}
                position="left-3  w-[10rem] -mt-3 ms-2"
              >
                {" "}
                <GetHeroIcon
                  name="InformationCircleIcon"
                  variant="outline"
                  className="w-6 h-6 ps-1 mt-0"
                />{" "}
              </Tooltip>
            )}
          </label>
        )}
        <div className="relative rounded-md shadow-sm w-full">
          {iconName && iconPosition === "leading" && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <GetHeroIcon
                name={iconName}
                className="w-5 h-5 text-secondary/50"
                aria-hidden="true"
                variant={iconVariant}
              />
            </div>
          )}
          <input
            maxLength={maxLength}
            disabled={inputdisable}
            defaultValue={value}
            onKeyDown={handleKeyDown}
            className={`dark:placeholder-white dark:bg-bkg dark:ring-bkg block w-full rounded-md border-0 dark:ring-secondary/20 py-1.5 focus:ring-inset ${
              iconName && iconPosition === "leading"
                ? "pl-10 pr-3"
                : iconPosition === "trailing"
                ? "pr-10 pl-3"
                : "px-3"
            } text-secondary shadow-sm ring-1 ring-inset ${
              error
                ? "ring-red-500 focus:ring-red-500"
                : "ring-border focus:ring-primary"
            } placeholder:text-secondary/50 sm:text-sm sm:leading-6 outline-none disabled:cursor-not-allowed disabled:bg-gray-300  disabled:text-gray-500 dark:text-gray-300  disabled:ring-gray-0
            dark:disabled:bg-gray-800`}
            {...(register ? register(name) : {})}
            onInput={(el) => {
              if (setterFunction) {
                setterFunction(el);
              }
            }}
            {...rest}
          />
          {iconName && iconPosition === "trailing" && !error && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <GetHeroIcon
                name={iconName}
                className="w-5 h-5  text-secondary/50"
                aria-hidden="true"
                variant={iconVariant}
              />
            </div>
          )}
          {error && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-7">
              <GetHeroIcon
                name="ExclamationCircleIcon"
                variant="solid"
                className="h-5 w-5 text-red-500"
                aria-hidden="true"
              />
            </div>
          )}
        </div>
        {error && errorMessage && (
          <p className="text-sm text-red-500">{errorMessage}</p>
        )}
      </div>
    );
  }
);

export default Input;
